import { type NavigationAnchor } from "@/components/layout/types";
import { useCallback, useMemo } from "react";

const filterItems = (items: NavigationAnchor[], search: string) => {
  return items
    .filter((item) => item.title.toLowerCase().includes(search))
    .filter((item) => Boolean(item.permission));
};

export const useMenuSearch = (
  groups: Record<string, NavigationAnchor[]>,
  searchQuery: string,
) => {
  const isSearchActive = searchQuery.length > 0;

  const filteredGroups = useMemo(() => {
    const newFilteredGroups: Record<string, NavigationAnchor[]> = {};
    Object.entries(groups).forEach(([groupKey, items]) => {
      const filteredItems = filterItems(items, searchQuery.toLowerCase());
      newFilteredGroups[groupKey] = filteredItems;
    });
    return newFilteredGroups;
  }, [groups, searchQuery]);

  const hasGroupItems = useCallback(
    (groupKey: string) => {
      if (!isSearchActive) {
        return true;
      }
      const filteredItems = filteredGroups[groupKey] || [];
      return filteredItems.length > 0;
    },
    [isSearchActive, filteredGroups],
  );

  const hasEmptySearchResults = useCallback(() => {
    if (!isSearchActive) {
      return false;
    }
    const filteredCount = Array.from(Object.values(filteredGroups)).reduce(
      (acc, items) => acc + items.length,
      0,
    );
    return filteredCount == 0;
  }, [isSearchActive, filteredGroups]);

  const getGroupItems = useCallback(
    (groupKey: string) => {
      return filteredGroups[groupKey] || [];
    },
    [filteredGroups],
  );

  return useMemo(
    () => ({
      hasGroupItems,
      hasEmptySearchResults,
      getGroupItems,
    }),
    [hasGroupItems, hasEmptySearchResults, getGroupItems],
  );
};
